/** @format */

/**
 * Breadcrumb Loaders
 *
 * This module contains loader functions for fetching breadcrumb data
 * using React Router's loader API. Each loader fetches minimal data
 * needed for breadcrumb display.
 */

import { patientsApi } from "@/lib/api/patients";
import { usersApi } from "@/lib/api/users";
import { visitsApi } from "@/lib/api/visits";
import { orlVisitsApi } from "@/lib/api/orlVisits";
import type { LoaderFunctionArgs } from "react-router-dom";

/**
 * Maximum number of retries for failed breadcrumb fetches
 */
const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 500;

/**
 * Retry helper with exponential backoff
 */
async function retryFetch<T>(
  fetchFn: () => Promise<T>,
  retries = MAX_RETRIES
): Promise<T> {
  try {
    return await fetchFn();
  } catch (error) {
    if (retries > 0) {
      await new Promise((resolve) =>
        setTimeout(resolve, RETRY_DELAY_MS * (MAX_RETRIES - retries + 1))
      );
      return retryFetch(fetchFn, retries - 1);
    }
    throw error;
  }
}

/**
 * Loader for patient breadcrumb data
 */
export async function patientBreadcrumbLoader({ params }: LoaderFunctionArgs) {
  const { id } = params;

  if (!id) {
    return { patient: null };
  }

  try {
    const patient = await retryFetch(() => patientsApi.getPatient(id));
    return {
      patient: {
        id: patient.id,
        name: patient.full_name || `${patient.first_name} ${patient.last_name}`,
      },
    };
  } catch (error) {
    console.error("Failed to load patient breadcrumb data:", error);
    // Return ID as fallback
    return {
      patient: {
        id,
        name: id,
      },
    };
  }
}

/**
 * Loader for user breadcrumb data
 */
export async function userBreadcrumbLoader({ params }: LoaderFunctionArgs) {
  const { userId } = params;

  if (!userId) {
    return { user: null };
  }

  try {
    const user = await retryFetch(() => usersApi.getUser(Number(userId)));
    return {
      user: {
        id: String(user.id),
        name: `${user.first_name} ${user.last_name}`.trim() || user.email,
      },
    };
  } catch (error) {
    console.error("Failed to load user breadcrumb data:", error);
    // Return ID as fallback
    return {
      user: {
        id: userId,
        name: userId,
      },
    };
  }
}

/**
 * Loader for visit breadcrumb data
 * Note: Visits need both patient ID and visit ID
 */
export async function visitBreadcrumbLoader({ params }: LoaderFunctionArgs) {
  const { id: patientId, visitId } = params;

  if (!patientId || !visitId) {
    return { visit: null };
  }

  try {
    const visit = await retryFetch(() =>
      visitsApi.getVisit(visitId, patientId)
    );
    return {
      visit: {
        id: visit.id,
        name: `Visit ${new Date(visit.visit_date).toLocaleDateString()}`,
      },
    };
  } catch (error) {
    console.error("Failed to load visit breadcrumb data:", error);
    // Return ID as fallback
    return {
      visit: {
        id: visitId,
        name: visitId,
      },
    };
  }
}

/**
 * Loader for ORL visit breadcrumb data
 * Note: ORL visits need both patient ID and visit ID
 */
export async function orlVisitBreadcrumbLoader({ params }: LoaderFunctionArgs) {
  const { id: patientId, visitId } = params;

  if (!patientId || !visitId) {
    return { orlVisit: null };
  }

  try {
    const visit = await retryFetch(() =>
      orlVisitsApi.getVisit(patientId, visitId)
    );
    return {
      orlVisit: {
        id: visit.id,
        name: `ORL visit ${new Date(visit.visit_date).toLocaleDateString()}`,
      },
    };
  } catch (error) {
    console.error("Failed to load ORL visit breadcrumb data:", error);
    // Return ID as fallback
    return {
      orlVisit: {
        id: visitId,
        name: visitId,
      },
    };
  }
}

/**
 * Combined loader for patient visits page
 * Loads both patient and basic visit list info
 */
export async function patientVisitsBreadcrumbLoader({
  params,
}: LoaderFunctionArgs) {
  const { id } = params;

  if (!id) {
    return { patient: null };
  }

  // Only load patient data for breadcrumbs
  return patientBreadcrumbLoader({ params } as LoaderFunctionArgs);
}

/**
 * Combined loader for visit detail/edit pages
 * Loads both patient and visit data
 */
export async function visitDetailBreadcrumbLoader({
  params,
}: LoaderFunctionArgs) {
  const { id: patientId, visitId } = params;

  if (!patientId || !visitId) {
    return { patient: null, visit: null };
  }

  try {
    // Load patient and visit in parallel
    const [patientData, visitData] = await Promise.all([
      patientBreadcrumbLoader({ params } as LoaderFunctionArgs),
      visitBreadcrumbLoader({ params } as LoaderFunctionArgs),
    ]);

    return {
      patient: patientData.patient,
      visit: visitData.visit,
    };
  } catch (error) {
    console.error("Failed to load breadcrumb data:", error);
    return {
      patient: { id: patientId, name: patientId },
      visit: visitId ? { id: visitId, name: visitId } : null,
    };
  }
}

/**
 * Combined loader for ORL visit detail/edit pages
 * Loads both patient and ORL visit data
 */
export async function orlVisitDetailBreadcrumbLoader({
  params,
}: LoaderFunctionArgs) {
  const { id: patientId, visitId } = params;

  if (!patientId || !visitId) {
    return { patient: null, orlVisit: null };
  }

  try {
    const [patientData, visitData] = await Promise.all([
      patientBreadcrumbLoader({ params } as LoaderFunctionArgs),
      orlVisitBreadcrumbLoader({ params } as LoaderFunctionArgs),
    ]);

    return {
      patient: patientData.patient,
      orlVisit: visitData.orlVisit,
    };
  } catch (error) {
    console.error("Failed to load ORL breadcrumb data:", error);
    return {
      patient: { id: patientId, name: patientId },
      orlVisit: visitId ? { id: visitId, name: visitId } : null,
    };
  }
}
